import type { ReceiptData, ReceiptPrintOptions, A4RenderModel, A4RenderItem } from '@matix/types';
import { formatEntryDate, pickName } from './format.js';
import { formatReceiptAddress } from './formatReceiptAddress.js';
import { amountInWords, amountInWordsAr } from './amountInWords.js';
import { buildZatcaQrSvg } from './zatcaQr.js';
import { code128Svg } from './code128.js';
import { orderTypeNameAr, orderTypeNameEn } from './orderTypeName.js';
import { headerPromotions, promotionLabel } from './promotionLines.js';

/**
 * ReceiptData → A4RenderModel: the field catalogue behind the A4 Print
 * Designer. Every token in `A4_FIELD_TOKENS` is filled here, money is kept
 * NUMERIC (the renderer splits dinars from fils per column), and the per-line
 * tax breakdown is derived the same way the default A4 sheet derives it —
 * header discount allocated pro rata, last line takes the rounding remainder —
 * so a designed sheet's columns add up to its totals exactly like the
 * ERP-matched one does.
 */

type CurrencyArg = string | { code: string; precision?: number };

const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?/;

interface DateParts { y: number; mo: number; d: number; h: number; mi: number; s: number; ms: number }

function dateParts(value: string): DateParts | null {
  const m = NAIVE_RE.exec((value ?? '').trim());
  if (m) {
    return {
      y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]),
      h: Number(m[4]), mi: Number(m[5]), s: Number(m[6] ?? 0),
      ms: Number((m[7] ?? '0').padEnd(3, '0').slice(0, 3)),
    };
  }
  const ms = Date.parse((value ?? '').trim());
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return { y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate(), h: d.getHours(), mi: d.getMinutes(), s: d.getSeconds(), ms: d.getMilliseconds() };
}

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

/** The Arabic words for a currency's units, for the amount-in-words line. */
const AR_UNITS: Record<string, { major: string; minor: string }> = {
  KWD: { major: 'دينار كويتي', minor: 'فلس' },
  BHD: { major: 'دينار بحريني', minor: 'فلس' },
  OMR: { major: 'ريال عماني', minor: 'بيسة' },
  SAR: { major: 'ريال', minor: 'هللة' },
  AED: { major: 'درهم', minor: 'فلس' },
  QAR: { major: 'ريال قطري', minor: 'درهم' },
  EGP: { major: 'جنيه', minor: 'قرش' },
  JOD: { major: 'دينار أردني', minor: 'قرش' },
};

export function buildA4DesignModel(
  data: ReceiptData,
  currency: CurrencyArg,
  po: ReceiptPrintOptions,
  arabic: boolean,
): A4RenderModel {
  const pc = data.printConfig;
  const code = typeof currency === 'string' ? currency : currency.code;
  const precision = typeof currency === 'string' ? 2 : (currency.precision ?? 2);
  const round = (n: number): number => {
    const f = 10 ** precision;
    return Math.round((n + Number.EPSILON) * f) / f;
  };
  const money = (n: number): string => (Number.isFinite(n) ? n : 0).toLocaleString('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision });

  // ── Per-line derivation (mirrors buildA4Html.priceLines) ──
  const lines = data.items;
  const grossOfLines = lines.reduce((s, l) => s + Math.abs(l.total), 0);
  const discount = Math.max(0, data.totals.discount ?? 0);
  const netTotal = Math.max(0, grossOfLines - discount);
  const rate =
    data.taxPercent != null && data.taxPercent > 0
      ? data.taxPercent
      : netTotal > 0 ? round((data.totals.tax / netTotal) * 100) : 0;

  let netRunning = 0;
  let taxRunning = 0;
  const items: A4RenderItem[] = lines.map((line, index) => {
    const lineGrossEx = Math.abs(line.total);
    const share = grossOfLines > 0 ? lineGrossEx / grossOfLines : 0;
    const isLast = index === lines.length - 1;
    let net = round(lineGrossEx - discount * share);
    if (isLast) net = round(netTotal - netRunning);
    netRunning = round(netRunning + net);
    let tax = round((net * rate) / 100);
    if (isLast) tax = round((data.totals.tax ?? 0) - taxRunning);
    taxRunning = round(taxRunning + tax);
    const sign = line.isReturn ? -1 : 1;
    return {
      code: line.code ?? null,
      barcode: line.barcode ?? null,
      name: (arabic ? line.nameAr || line.name : line.name || line.nameAr || '').trim(),
      nameAr: (arabic ? line.name : line.nameAr) ?? null,
      unit: (arabic ? line.unitAr || line.unit : line.unit || line.unitAr) ?? null,
      qty: Math.abs(line.qty),
      unitPrice: Math.abs(line.price),
      discount: line.discount ?? 0,
      net: sign * net,
      tax: sign * tax,
      gross: sign * round(net + tax),
      total: sign * lineGrossEx,
      ...(line.mods && line.mods.length > 0 ? { mods: line.mods } : {}),
      note: line.note ?? null,
      salesman: pickName(line.salesman, line.salesmanAr) || null,
      ...(line.isReturn ? { isReturn: true } : {}),
    };
  });

  // ── Totals ──
  const subtotal = data.totals.subtotal ?? grossOfLines;
  const delivery = data.totals.deliveryFee ?? 0;
  const afterDiscount = round(subtotal - discount + delivery);
  const tax = data.totals.tax ?? 0;
  const total = data.totals.total ?? round(afterDiscount + tax);
  const paid = data.payments.reduce((s, p) => s + (p.amount || 0), 0);
  const change = data.changeDue && data.changeDue > 0 ? data.changeDue : 0;

  const arUnits = AR_UNITS[code.toUpperCase()] ?? { major: code, minor: '' };
  const totalInWords = {
    en: amountInWords(total, code, precision),
    ar: amountInWordsAr(total, precision, arUnits.major, arUnits.minor),
  };

  // ── Dates ──
  const p = dateParts(data.entryDate);
  const orderDate = p ? `${pad(p.d)}-${pad(p.mo)}-${p.y}` : (data.entryDate ?? '').trim();
  const orderTime = p ? `${pad(p.h)}:${pad(p.mi)}` : '';
  const now = new Date();
  const printDate = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

  // ── Document title: a pure return is a return document; everything else a cash sale ──
  const ex = data.exchange;
  const allReturn = Boolean(ex) && (ex?.soldTotal ?? 0) <= 0 && (ex?.returnedTotal ?? 0) > 0;
  const onAccount = data.payments.length === 1 && /unpaid|credit|آجل|على الحساب/i.test(data.payments[0]?.typeName ?? '');
  const titleEn = allReturn ? 'Sales Return' : onAccount ? 'Credit Sales' : 'Cash Sales';
  const titleAr = allReturn ? 'مرتجع مبيعات' : onAccount ? 'مبيعات آجلة' : 'مبيعات نقدية';

  // ── Memo is the receipt's OWN memo (Inv_Receipt_h.Memo) ──
  // It used to be a synthesised "table — K-Net 28.000" summary, which a
  // tenant whose ERP receipts carry a real memo read as wrong data in the
  // Memo box. The payment summary is its own token now (`payments`), so a
  // design that wants it binds it explicitly.
  const memo = (data.memo ?? '').trim();
  // The till's own two boxes. `reference_no` is the SAME column as `memo`
  // (Inv_Receipt_h.Memo) under the name the cashier sees; `notes` is what the
  // ERP's report prints under الملاحظات, resolved server-side out of
  // Notes / Reference_No. See ReceiptData in @matix/types.
  const referenceNo = (data.referenceNo ?? data.memo ?? '').trim();
  const notes = (data.notes ?? '').trim();
  // "Knet" — the method alone. The ERP's own header grid labels this الدفع
  // and prints no amount there; the amount is in the totals box.
  const paymentMethods = data.payments
    .map((pm) => (pickName(pm.typeName, pm.typeNameAr) || pm.typeName || '').trim())
    .filter(Boolean)
    .join(' · ');
  const payments = data.payments
    .map((pm) => `${(pm.typeName || pm.typeNameAr) ?? ''} ${money(pm.amount)}`.trim())
    .join(' · ');
  // The legacy till's A4 payments line — "Online KENT :57.000".
  const paymentsColon = data.payments
    .map((pm) => `${(pm.typeName || pm.typeNameAr) ?? ''} :${money(pm.amount)}`.trim())
    .join(' · ');

  const customerAddress =
    formatReceiptAddress(data.customer?.addressDetail, arabic).join(' · ') ||
    (data.customer?.address ?? '').trim();
  const salesman = items.map((it) => it.salesman).find((s) => s && s.trim()) ?? '';

  const fields: Record<string, string> = {
    document_title: titleEn,
    document_title_ar: titleAr,
    order_no: String(data.orderCode ?? data.orderId),
    // The cash receipt voucher (سند قبض) is booked in the ERP, not by the till.
    receipt_no: '',
    reference: ex ? String(ex.saleEntryNo ?? ex.returnEntryNo ?? '') : '',
    order_date: orderDate,
    order_time: orderTime,
    datetime: formatEntryDate(data.entryDate, arabic),
    datetime_short: p ? `${pad(p.d)}/${pad(p.mo)}/${p.y} ${pad(p.h)}:${pad(p.mi)}` : '',
    // The legacy till's A4 "Date:" line — "02-10-2026 4:23:56 pm".
    datetime_seconds: p ? `${pad(p.d)}-${pad(p.mo)}-${p.y} ${((p.h + 11) % 12) + 1}:${pad(p.mi)}:${pad(p.s)} ${p.h < 12 ? 'am' : 'pm'}` : '',
    order_type: orderTypeNameEn(data.orderType),
    order_type_ar: orderTypeNameAr(data.orderType),
    delivery_date: data.deliveryDate ? formatEntryDate(data.deliveryDate, arabic) : '',
    memo,
    reference_no: referenceNo,
    notes,
    payments,
    payments_colon: paymentsColon,
    payment_methods: paymentMethods,
    table: data.tableLabel ?? '',
    cashier_name: data.cashier.name ?? `#${data.cashier.id}`,
    salesman_name: salesman,
    reprint_label: data.isReprint ? (arabic ? 'إعادة طباعة' : 'REPRINT') : '',
    print_date: printDate,

    company_name: pc.companyNameEn ?? pc.companyNameAr ?? '',
    company_name_ar: pc.companyNameAr ?? pc.companyNameEn ?? '',
    company_address: pc.companyAddressEn ?? pc.companyAddressAr ?? '',
    company_address_ar: pc.companyAddressAr ?? pc.companyAddressEn ?? '',
    company_contact: pc.companyContactEn ?? pc.companyContactAr ?? '',
    company_contact_ar: pc.companyContactAr ?? pc.companyContactEn ?? '',
    branch_name: pc.branchNameEn ?? pc.branchNameAr ?? '',
    branch_name_ar: pc.branchNameAr ?? pc.branchNameEn ?? '',
    branch_address: pc.branchAddressLine ?? '',
    tax_number: pc.taxRegisterNo ?? '',
    cr_number: pc.commercialNo ?? pc.registerNo ?? '',

    customer_name: pickName(data.customer?.name, data.customer?.nameAr) ?? '',
    customer_name_ar: (data.customer?.nameAr ?? data.customer?.name ?? '').trim(),
    customer_phone: data.customer?.phone ?? '',
    customer_address: customerAddress,
    customer_tax_number: data.customer?.taxNumber ?? '',

    subtotal: money(subtotal),
    discount: discount > 0 ? money(discount) : '',
    promotion_names: headerPromotions(data)
      .filter((pr) => pr.amount > 0)
      .map((pr) => promotionLabel(pr, arabic))
      .filter((n) => n.length > 0)
      .join(' + '),
    delivery: delivery > 0 ? money(delivery) : '',
    tax: tax > 0 ? money(tax) : '',
    tax_percent: rate > 0 ? String(Number(rate.toFixed(2))) : '0',
    total: money(total),
    paid: money(paid),
    change: change > 0 ? money(change) : '',
    // The legacy sheets print "Change: 0.000"; {{change}} stays blank at zero for the designs that hide the row.
    change_amount: money(change),
    total_in_words: totalInWords.en,
    total_in_words_ar: totalInWords.ar,
    currency: code,
    item_count: String(items.length),
  };

  // ── ZATCA QR: only a tenant with a VAT number gets one ──
  const vat = (pc.taxRegisterNo ?? '').trim();
  const qrSvg = vat
    ? buildZatcaQrSvg({
        sellerName: ((pc.companyNameAr ?? '') || (pc.companyNameEn ?? '')).trim(),
        vatNumber: vat,
        timestamp: p ? `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}.${pad(p.ms, 3)}` : '1970-01-01T00:00:00.000',
        total: total.toFixed(2),
        vat: tax.toFixed(2),
        sizeMm: 30,
      })
    : null;

  return {
    fields,
    items,
    totals: { subtotal, discount, delivery, afterDiscount, tax, total, paid, change },
    totalInWords,
    currency: { code, precision },
    ...(headerPromotions(data).some((pr) => pr.amount > 0)
      ? {
          promotions: headerPromotions(data)
            .filter((pr) => pr.amount > 0)
            .map((pr) => ({ nameEn: (pr.name ?? '').trim(), nameAr: (pr.nameAr ?? '').trim(), amount: pr.amount })),
        }
      : {}),
    payments: data.payments.map((pm) => ({ label: pickName(pm.typeName, pm.typeNameAr) || pm.typeName, amount: pm.amount })),
    logoDataUrl: pc.receiptLogo,
    menuLogoDataUrl: pc.menuLogo ?? null,
    qrSvg,
    // A few hundred bytes of SVG; printed only where a design places a
    // `barcode` element.
    barcodeSvg: code128Svg(String(data.orderCode ?? data.orderId), { showText: false }),
    direction: arabic ? 'rtl' : 'ltr',
    arabicLabels: arabic,
    printOptions: po,
    isReprint: Boolean(data.isReprint),
  };
}
